import { useCallback, useMemo, type FC } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  Button,
  Form,
  Input,
  Select,
} from "../../../shared";
import {
  formatTimezoneDisplay,
  getAvailableTimezones,
} from "../../../../utils/dateService";

import {
  CONTACT_INFORMATION_INITIAL_VALUES,
  CONTACT_INFORMATION_REQUIRED_FIELDS,
  US_STATE_OPTIONS,
  WEBSITE_URL_PATTERN,
  getDefaultTimezone,
  type ContactInformationFormValues,
} from "./contactInformationData";

import { HIDE_PORTAL_ACCESS_FIELDS } from "./contactInformationData";
import styles from "./ContactInformation.module.css";

export type ContactInformationProps = {
  initialValues?: Partial<ContactInformationFormValues>;
  onSubmit?: (values: ContactInformationFormValues) => void;
  onValuesChange?: (values: ContactInformationFormValues) => void;
  // Skip the internal Continue button — modal consumers own their footer.
  hideFooter?: boolean;
  // Drop email + Portal access timezone in edit mode.
  hideImmutableFields?: boolean;
};

const ContactInformation: FC<ContactInformationProps> = ({
  initialValues,
  onSubmit,
  onValuesChange,
  hideFooter = false,
  hideImmutableFields = false,
}) => {
  const [form] = Form.useForm<ContactInformationFormValues>();

  // Subscribe via a selector that returns a boolean — antd's `useWatch` uses
  // `Object.is` to gate re-renders, so a primitive lets the component skip
  // updates while typing in fields that aren't part of the gate. Full
  // validation still runs on submit; this is purely a CTA hint.
  const canContinue = Form.useWatch(
    (values) =>
      Boolean(values) &&
      CONTACT_INFORMATION_REQUIRED_FIELDS.every((field) => {
        const value = values?.[field];
        return typeof value === "string" && value.trim().length > 0;
      }),
    form,
  );

  const mergedInitialValues = useMemo(() => {
    // Falling back to the browser tz happens once at mount; if the parent
    // later hydrates `initialValues.timezone` (e.g. user came back from a
    // later step), antd's form keeps the user's prior pick — we only fill
    // the blank when nothing else is set.
    const merged = {
      ...CONTACT_INFORMATION_INITIAL_VALUES,
      ...initialValues,
    } as ContactInformationFormValues;
    if (!merged.timezone) merged.timezone = getDefaultTimezone();
    return merged;
  }, [initialValues]);

  // Timezone options are derived from the runtime `Intl.supportedValuesOf`
  // and won't change while the page is mounted — memo once.
  const timezoneOptions = useMemo(
    () =>
      getAvailableTimezones().map((tz: string) => ({
        value: tz,
        label: formatTimezoneDisplay(tz),
      })),
    [],
  );

  const handleFinish = useCallback(
    (values: ContactInformationFormValues) => {
      onSubmit?.(values);
    },
    [onSubmit],
  );

  // antd fires `onValuesChange(changedValues, allValues)` — propagate the
  // full snapshot so the parent doesn't have to re-merge with its previous
  // state on every keystroke.
  const handleValuesChange = useCallback(
    (_changed: Partial<ContactInformationFormValues>, all: ContactInformationFormValues) => {
      onValuesChange?.(all);
    },
    [onValuesChange],
  );

  return (
    <Form<ContactInformationFormValues>
      form={form}
      initialValues={mergedInitialValues}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      className={styles.form}
      data-testid="new-affiliate-contact-information-form"
    >
      <section
        className={styles.section}
        aria-labelledby="new-affiliate-contact-information-title"
      >
        <h2
          id="new-affiliate-contact-information-title"
          className={styles.sectionTitle}
        >
          {AppStrings.NEW_AFFILIATE_CONTACT_INFORMATION}
        </h2>

        <div className={styles.row}>
          <Form.Item
            name="name"
            label={`${AppStrings.NAME}*`}
            rules={[
              {
                required: true,
                message: AppStrings.NEW_AFFILIATE_VALIDATION_REQUIRED_NAME,
              },
              Form.validators.noWhitespace(
                AppStrings.NEW_AFFILIATE_VALIDATION_REQUIRED_NAME,
              ),
            ]}
          >
            <Input
              placeholder={AppStrings.NEW_AFFILIATE_NAME_PLACEHOLDER}
              data-testid="new-affiliate-name-input"
            />
          </Form.Item>

          {!hideImmutableFields && (
            <Form.Item
              name="email"
              label={`${AppStrings.EMAIL}*`}
              rules={[
                {
                  required: true,
                  message: AppStrings.VALIDATION_REQUIRED_EMAIL,
                },
                {
                  type: "email",
                  message: AppStrings.VALIDATION_INVALID_EMAIL,
                },
              ]}
            >
              <Input
                type="email"
                placeholder={AppStrings.NEW_AFFILIATE_EMAIL_PLACEHOLDER}
                data-testid="new-affiliate-email-input"
              />
            </Form.Item>
          )}
        </div>

        <div className={styles.row}>
          <Form.Item name="phone" label={AppStrings.PHONE}>
            <Input
              placeholder={AppStrings.NEW_AFFILIATE_PHONE_PLACEHOLDER}
              data-testid="new-affiliate-phone-input"
              inputMode="tel"
            />
          </Form.Item>

          <Form.Item
            name="websiteUrl"
            label={AppStrings.AFFILIATE_WEBSITE_URL}
            rules={[
              {
                pattern: WEBSITE_URL_PATTERN,
                message:
                  AppStrings.NEW_AFFILIATE_VALIDATION_INVALID_WEBSITE_URL,
              },
            ]}
          >
            <Input
              placeholder={
                AppStrings.NEW_AFFILIATE_WEBSITE_URL_PLACEHOLDER
              }
              data-testid="new-affiliate-website-url-input"
              inputMode="url"
            />
          </Form.Item>
        </div>
      </section>

      {!hideImmutableFields && !HIDE_PORTAL_ACCESS_FIELDS && (
        <>
          <div
            className={styles.divider}
            role="separator"
            aria-hidden="true"
          />
          <section
            className={styles.section}
            aria-labelledby="new-affiliate-pam-access-title"
          >
            <h2
              id="new-affiliate-pam-access-title"
              className={styles.sectionTitle}
            >
              {AppStrings.NEW_AFFILIATE_PAM_ACCESS}
            </h2>

            <div className={styles.row}>
              <Form.Item
                name="timezone"
                label={`${AppStrings.NEW_AFFILIATE_TIMEZONE}*`}
                rules={[
                  {
                    required: true,
                    message: AppStrings.VALIDATION_REQUIRED_TIMEZONE,
                  },
                ]}
              >
                <Select
                  placeholder={
                    AppStrings.NEW_AFFILIATE_TIMEZONE_PLACEHOLDER
                  }
                  options={timezoneOptions}
                  showSearch
                  optionFilterProp="label"
                  data-testid="new-affiliate-timezone-select"
                />
              </Form.Item>
            </div>
          </section>
        </>
      )}

      <div className={styles.divider} role="separator" aria-hidden="true" />

      <section
        className={styles.section}
        aria-labelledby="new-affiliate-address-title"
      >
        <h2
          id="new-affiliate-address-title"
          className={styles.sectionTitle}
        >
          {AppStrings.ADDRESS}
        </h2>

        <Form.Item
          name="addressLine1"
          label={AppStrings.NEW_AFFILIATE_ADDRESS_LINE_1}
        >
          <Input
            placeholder={
              AppStrings.NEW_AFFILIATE_ADDRESS_LINE_1_PLACEHOLDER
            }
            data-testid="new-affiliate-address-line-1-input"
          />
        </Form.Item>

        <Form.Item
          name="addressLine2"
          label={AppStrings.NEW_AFFILIATE_ADDRESS_LINE_2}
        >
          <Input
            placeholder={
              AppStrings.NEW_AFFILIATE_ADDRESS_LINE_2_PLACEHOLDER
            }
            data-testid="new-affiliate-address-line-2-input"
          />
        </Form.Item>

        <div className={styles.row}>
          <Form.Item
            name="city"
            label={AppStrings.NEW_AFFILIATE_CITY}
          >
            <Input
              placeholder={AppStrings.NEW_AFFILIATE_CITY_PLACEHOLDER}
              data-testid="new-affiliate-city-input"
            />
          </Form.Item>

          <Form.Item
            name="zipCode"
            label={AppStrings.NEW_AFFILIATE_ZIP_CODE}
          >
            <Input
              placeholder={AppStrings.NEW_AFFILIATE_ZIP_CODE_PLACEHOLDER}
              data-testid="new-affiliate-zip-code-input"
            />
          </Form.Item>
        </div>

        <div className={styles.row}>
          <Form.Item name="state" label={AppStrings.STATE}>
            <Select
              placeholder={AppStrings.SELECT_STATE}
              options={US_STATE_OPTIONS as unknown as { value: string; label: string }[]}
              allowClear
              showSearch
              optionFilterProp="label"
              data-testid="new-affiliate-state-select"
            />
          </Form.Item>
        </div>
      </section>

      {!hideFooter && (
        <>
          <div
            className={styles.divider}
            role="separator"
            aria-hidden="true"
          />

          <div className={styles.footer}>
            <Button
              intent="primary"
              htmlType="submit"
              disabled={!canContinue}
              data-testid="new-affiliate-contact-information-submit"
            >
              {AppStrings.CONTINUE}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};

export default ContactInformation;
